import './ColumnHeader.scss';
import cn from 'classnames';
import { RowData } from '@tanstack/react-table';
import { LegacyColumn } from '@tanstack/react-table/legacy';
import { ReactNode } from 'react';
import { MdArrowDownward, MdArrowUpward, MdSwapVert } from 'react-icons/md';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { MultiSelectFilter } from './MultiSelectFilter';
import { RangeFilter } from './RangeFilter';

interface ColumnHeaderProps<TData extends RowData> {
  column: LegacyColumn<TData>;
  label: string;
  filterType?: 'multiselect' | 'range';
  displayDecimals?: number;
  /** Custom display for the header (defaults to label) */
  displayLabel?: ReactNode;
}

/**
 * Unified column header with filtering and sorting
 * - Supports both multi-select and range filtering
 * - Sort icon: Changes based on sort direction (unsorted/asc/desc)
 */
export const ColumnHeader = <TData extends RowData>({
  column,
  label,
  filterType = 'multiselect',
  displayDecimals = 0,
  displayLabel
}: ColumnHeaderProps<TData>) => {
  const sortDirection = column.getIsSorted();

  const SORT_ICONS = { asc: MdArrowUpward, desc: MdArrowDownward };
  const SortIcon = sortDirection ? SORT_ICONS[sortDirection] : MdSwapVert;

  return (
    <div className='column-header'>
      <span className='column-label'>{displayLabel ?? label}</span>

      {filterType === 'range' ? (
        <RangeFilter column={column} label={label} displayDecimals={displayDecimals} />
      ) : (
        <MultiSelectFilter column={column} label={label} />
      )}

      {column.getCanSort() && (
        <InteractiveDiv
          className={cn('sort-icon', { active: Boolean(sortDirection) })}
          onClick={column.getToggleSortingHandler()}
        >
          <SortIcon />
        </InteractiveDiv>
      )}
    </div>
  );
};
